import {
  CLIENT_PLATFORM_HEADER,
  resolveClientPlatform,
} from "./platform/runtime.js";

const SHORT_CACHE_TTL_MS = 60 * 1000;
const MEDIUM_CACHE_TTL_MS = 60 * 1000;
const LONG_CACHE_TTL_MS = 2 * 60 * 1000;
const EXERCISE_CACHE_TTL_MS = 10 * 60 * 1000;
const MAX_RESPONSE_CACHE_SIZE = 160;
const DEV_API_METRICS_ENABLED = typeof window !== "undefined" && !!import.meta?.env?.DEV;

function stableSerialize(value) {
  if (value === undefined) {
    return "";
  }
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map((entry) => stableSerialize(entry)).join(",")}]`;
  }
  return `{${Object.keys(value)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableSerialize(value[key])}`)
    .join(",")}}`;
}

function normalizeIdArray(values) {
  return Array.from(
    new Set(
      (values || [])
        .map((value) => String(value || "").trim())
        .filter(Boolean),
    ),
  ).sort();
}

function normalizeSearchQuery(query) {
  return String(query || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function isWorkoutDetailPath(path) {
  return /^\/api\/workouts\/[^/?]+$/.test(String(path || ""));
}

function parseExerciseSearchPath(path) {
  if (!String(path || "").startsWith("/api/exercises/search")) {
    return null;
  }

  try {
    const url = new URL(path, "https://liftflow.local");
    return {
      query: normalizeSearchQuery(url.searchParams.get("q") || ""),
      includeCustom: url.searchParams.get("includeCustom") !== "false",
    };
  } catch (_) {
    return null;
  }
}

function buildWorkoutMutationInvalidationMatchers(extraMatchers = []) {
  return [
    "/api/me/bootstrap",
    "/api/workouts?",
    "/api/workouts/by-date/",
    "/api/workouts/calendar",
    "/api/workouts/by-exercise/",
    ...extraMatchers,
  ];
}

function matchesExerciseSearchItem(item, normalizedQuery) {
  if (!item || !normalizedQuery) {
    return false;
  }

  const candidates = [
    item.id,
    item.nameKo,
    item.nameEn,
    item.abbr,
    ...(Array.isArray(item.aliases) ? item.aliases : []),
  ];

  return candidates.some((value) => normalizeSearchQuery(value) === normalizedQuery);
}

export class ApiClient {
  constructor(getIdToken) {
    this.getIdToken = getIdToken;
    this.responseCache = new Map();
    this.inflightRequests = new Map();
    this.networkRequestCount = 0;
    this.endpointCallCounts = new Map();
    this.journeyCallCounts = new Map();
  }

  emitNetworkLoading(delta) {
    if (typeof window === "undefined") return;
    window.dispatchEvent(
      new CustomEvent("app:network-loading", {
        detail: { delta },
      }),
    );
  }

  clearCache() {
    this.responseCache.clear();
    this.inflightRequests.clear();
  }

  invalidateCache(matchers = []) {
    if (!Array.isArray(matchers) || matchers.length <= 0) {
      return;
    }

    const shouldInvalidate = (key) =>
      matchers.some((matcher) => typeof matcher === "string" && matcher && key.includes(matcher));

    Array.from(this.responseCache.keys()).forEach((key) => {
      if (shouldInvalidate(key)) {
        this.responseCache.delete(key);
      }
    });

    Array.from(this.inflightRequests.keys()).forEach((key) => {
      if (shouldInvalidate(key)) {
        this.inflightRequests.delete(key);
      }
    });
  }

  trimCache(now = Date.now()) {
    for (const [key, entry] of this.responseCache.entries()) {
      if ((Number(entry?.expiresAt) || 0) <= now) {
        this.responseCache.delete(key);
      }
    }

    if (this.responseCache.size <= MAX_RESPONSE_CACHE_SIZE) {
      return;
    }

    const entries = Array.from(this.responseCache.entries()).sort(
      (a, b) => (Number(a[1]?.expiresAt) || 0) - (Number(b[1]?.expiresAt) || 0),
    );
    const overflow = this.responseCache.size - MAX_RESPONSE_CACHE_SIZE;
    for (let index = 0; index < overflow; index += 1) {
      const cacheKey = entries[index]?.[0];
      if (cacheKey) {
        this.responseCache.delete(cacheKey);
      }
    }
  }

  buildExerciseSearchPath(query, options = {}) {
    const normalizedQuery = normalizeSearchQuery(query);
    const params = new URLSearchParams();
    params.set("q", normalizedQuery);
    if (options.includeCustom === false) {
      params.set("includeCustom", "false");
    }
    return `/api/exercises/search?${params.toString()}`;
  }

  getCachedResponse(cacheKey, now = Date.now()) {
    if (!cacheKey) {
      return null;
    }

    this.trimCache(now);
    const cached = this.responseCache.get(cacheKey);
    if (cached && Number(cached.expiresAt) > now) {
      return cached.data;
    }

    this.responseCache.delete(cacheKey);
    return null;
  }

  findCachedExerciseSearchMatch(query, options = {}) {
    const normalizedQuery = normalizeSearchQuery(query);
    if (!normalizedQuery || normalizedQuery.length < 2) {
      return null;
    }

    const searchPath = this.buildExerciseSearchPath(normalizedQuery, options);
    const cachePolicy = this.resolveCachePolicy(searchPath, { method: "GET" });
    if (!cachePolicy) {
      return null;
    }

    const cacheKey = this.createCacheKey(
      "GET",
      cachePolicy.cachePath || searchPath,
      cachePolicy.cacheBody,
    );
    const cached = this.getCachedResponse(cacheKey);
    const items = Array.isArray(cached?.items) ? cached.items : [];
    return (
      items.find((item) => matchesExerciseSearchItem(item, normalizedQuery)) ||
      items[0] ||
      null
    );
  }

  describeEndpoint(method, path) {
    const safeMethod = String(method || "GET").toUpperCase();
    const safePath = String(path || "");

    if (safePath === "/api/me/bootstrap") {
      return `${safeMethod} /api/me/bootstrap`;
    }
    if (safePath.startsWith("/api/exercises/search")) {
      return `${safeMethod} /api/exercises/search`;
    }
    if (safePath.startsWith("/api/workouts/latest-item/")) {
      return `${safeMethod} /api/workouts/latest-item/:exerciseId`;
    }
    if (safePath === "/api/workouts/latest-items") {
      return `${safeMethod} /api/workouts/latest-items`;
    }
    if (isWorkoutDetailPath(safePath)) {
      return `${safeMethod} /api/workouts/:workoutId`;
    }
    if (safePath.startsWith("/api/workouts/by-date/")) {
      return `${safeMethod} /api/workouts/by-date/:date`;
    }
    if (safePath.startsWith("/api/workouts/calendar")) {
      return `${safeMethod} /api/workouts/calendar`;
    }
    if (safePath.startsWith("/api/workouts/by-exercise/")) {
      return `${safeMethod} /api/workouts/by-exercise/:exerciseId`;
    }
    if (safePath.startsWith("/api/programs/") && safePath.endsWith("/activate")) {
      return `${safeMethod} /api/programs/:programId/activate`;
    }
    if (safePath.startsWith("/api/programs/")) {
      return `${safeMethod} /api/programs/:programId`;
    }
    if (safePath.startsWith("/api/workouts/") && safePath.includes("/items/") && safePath.endsWith("/sets")) {
      return `${safeMethod} /api/workouts/:workoutId/items/:order/sets`;
    }
    if (safePath.startsWith("/api/workouts/") && safePath.includes("/items/reorder")) {
      return `${safeMethod} /api/workouts/:workoutId/items/reorder`;
    }
    if (safePath.startsWith("/api/workouts/") && safePath.includes("/items/") && safePath.includes("/sets/")) {
      return `${safeMethod} /api/workouts/:workoutId/items/:order/sets/:setIndex`;
    }
    if (safePath.startsWith("/api/workouts/") && safePath.includes("/items/")) {
      return `${safeMethod} /api/workouts/:workoutId/items/:order`;
    }
    if (safePath.startsWith("/api/workouts?")) {
      return `${safeMethod} /api/workouts`;
    }
    if (safePath.startsWith("/api/analytics/volume?")) {
      return `${safeMethod} /api/analytics/volume`;
    }
    return `${safeMethod} ${safePath.split("?")[0]}`;
  }

  recordNetworkRequest(method, path) {
    this.networkRequestCount += 1;
    const endpoint = this.describeEndpoint(method, path);
    this.endpointCallCounts.set(endpoint, (this.endpointCallCounts.get(endpoint) || 0) + 1);
  }

  startJourney(name) {
    return {
      name: String(name || "").trim() || "unnamed",
      networkRequestBaseline: this.networkRequestCount,
      startedAt: Date.now(),
      completed: false,
    };
  }

  finishJourney(token, status = "ok") {
    if (!token || token.completed) {
      return;
    }

    token.completed = true;
    const name = String(token.name || "unnamed");
    const networkCalls = Math.max(0, this.networkRequestCount - Number(token.networkRequestBaseline || 0));
    const durationMs = Math.max(0, Date.now() - Number(token.startedAt || Date.now()));
    const summary = this.journeyCallCounts.get(name) || {
      runs: 0,
      totalNetworkCalls: 0,
      statuses: {},
    };

    summary.runs += 1;
    summary.totalNetworkCalls += networkCalls;
    summary.lastNetworkCalls = networkCalls;
    summary.lastDurationMs = durationMs;
    summary.statuses[status] = (summary.statuses[status] || 0) + 1;
    this.journeyCallCounts.set(name, summary);

    if (DEV_API_METRICS_ENABLED) {
      console.info("[api-journey]", name, {
        status,
        networkCalls,
        durationMs,
        endpointCallCounts: Object.fromEntries(this.endpointCallCounts.entries()),
      });
    }
  }

  async trackJourney(name, callback) {
    const token = this.startJourney(name);
    try {
      const result = await callback(token);
      this.finishJourney(token, "ok");
      return result;
    } catch (error) {
      this.finishJourney(token, "error");
      throw error;
    }
  }

  resolveCachePolicy(path, options = {}) {
    const method = String(options.method || "GET").toUpperCase();

    if (method === "GET") {
      if (path === "/api/me/bootstrap") {
        return { ttlMs: SHORT_CACHE_TTL_MS, cachePath: path };
      }
      if (path === "/api/me/custom-exercises") {
        return { ttlMs: LONG_CACHE_TTL_MS, cachePath: path };
      }
      if (path === "/api/programs" || path.startsWith("/api/programs/")) {
        return { ttlMs: LONG_CACHE_TTL_MS, cachePath: path };
      }
      if (
        path.startsWith("/api/workouts?") ||
        path.startsWith("/api/workouts/by-date/") ||
        path.startsWith("/api/workouts/calendar") ||
        path.startsWith("/api/workouts/by-exercise/")
      ) {
        return { ttlMs: MEDIUM_CACHE_TTL_MS, cachePath: path };
      }
      if (isWorkoutDetailPath(path)) {
        return { ttlMs: MEDIUM_CACHE_TTL_MS, cachePath: path };
      }
      if (path.startsWith("/api/workouts/latest-item/")) {
        return { ttlMs: MEDIUM_CACHE_TTL_MS, cachePath: path };
      }
      if (path.startsWith("/api/exercises/search")) {
        const parsed = parseExerciseSearchPath(path);
        if (!parsed?.query || parsed.query.length < 2) {
          return null;
        }
        return {
          ttlMs: EXERCISE_CACHE_TTL_MS,
          cachePath: "/api/exercises/search",
          cacheBody: {
            q: parsed.query,
            includeCustom: parsed.includeCustom,
          },
        };
      }
      if (path.startsWith("/api/exercises/") && !path.startsWith("/api/exercises/search")) {
        return { ttlMs: EXERCISE_CACHE_TTL_MS, cachePath: path };
      }
      if (path.startsWith("/api/analytics/volume?")) {
        return { ttlMs: LONG_CACHE_TTL_MS, cachePath: path };
      }
      return null;
    }

    if (method === "POST") {
      if (path === "/api/exercises/batch") {
        const ids = normalizeIdArray(options.body?.ids);
        if (ids.length <= 0) {
          return null;
        }
        return {
          ttlMs: EXERCISE_CACHE_TTL_MS,
          cachePath: path,
          cacheBody: { ids },
        };
      }

      if (path === "/api/workouts/latest-items") {
        const exerciseIds = normalizeIdArray(options.body?.exerciseIds);
        if (exerciseIds.length <= 0) {
          return null;
        }

        const excludeWorkoutId = String(options.body?.excludeWorkoutId || "").trim();
        return {
          ttlMs: MEDIUM_CACHE_TTL_MS,
          cachePath: path,
          cacheBody: excludeWorkoutId
            ? {
                exerciseIds,
                excludeWorkoutId,
              }
            : {
                exerciseIds,
              },
        };
      }
    }

    return null;
  }

  createCacheKey(method, path, body) {
    const serializedBody = stableSerialize(body);
    return serializedBody ? `${method} ${path} ${serializedBody}` : `${method} ${path}`;
  }

  async request(path, options = {}) {
    const method = String(options.method || "GET").toUpperCase();
    const cachePolicy = this.resolveCachePolicy(path, {
      ...options,
      method,
    });
    const cacheKey = cachePolicy
      ? this.createCacheKey(method, cachePolicy.cachePath || path, cachePolicy.cacheBody)
      : "";
    const now = Date.now();

    if (cachePolicy && !options.forceRefresh) {
      const cached = this.getCachedResponse(cacheKey, now);
      if (cached !== null) {
        return cached;
      }

      const inflight = this.inflightRequests.get(cacheKey);
      if (inflight) {
        return inflight;
      }
    }

    const requestPromise = (async () => {
      this.emitNetworkLoading(1);
      try {
        const token = await this.getIdToken();
        const headers = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          [CLIENT_PLATFORM_HEADER]: resolveClientPlatform(),
          ...(options.headers || {}),
        };

        this.recordNetworkRequest(method, path);
        const response = await fetch(path, {
          method,
          headers,
          cache: cachePolicy ? "default" : "no-store",
          body: options.body ? JSON.stringify(options.body) : undefined,
        });

        const json = await response.json().catch(() => ({}));

        if (!response.ok || !json.ok) {
          const message = json?.error?.message || `API 요청 실패 (${response.status})`;
          throw new Error(message);
        }

        const data = json.data;
        if (cachePolicy) {
          this.responseCache.set(cacheKey, {
            data,
            expiresAt: Date.now() + cachePolicy.ttlMs,
          });
          this.trimCache();
        }

        return data;
      } finally {
        this.emitNetworkLoading(-1);
        if (cacheKey) {
          this.inflightRequests.delete(cacheKey);
        }
      }
    })();

    if (cachePolicy && !options.forceRefresh) {
      this.inflightRequests.set(cacheKey, requestPromise);
    }

    return requestPromise;
  }

  async requestAndInvalidate(path, options = {}, matchers = []) {
    const result = await this.request(path, options);
    this.invalidateCache(matchers);
    return result;
  }

  getBootstrap() {
    return this.request("/api/me/bootstrap");
  }

  updateProfile(payload) {
    return this.requestAndInvalidate(
      "/api/me/profile",
      {
        method: "PATCH",
        body: payload,
      },
      ["/api/me/bootstrap"],
    );
  }

  updateSettings(payload) {
    return this.requestAndInvalidate(
      "/api/me/settings",
      {
        method: "PATCH",
        body: payload,
      },
      ["/api/me/bootstrap"],
    );
  }

  registerPushSubscription(subscription) {
    return this.requestAndInvalidate(
      "/api/me/push-subscriptions",
      {
        method: "POST",
        body: { subscription },
      },
      ["/api/me/bootstrap"],
    );
  }

  deletePushSubscription(subscriptionId) {
    return this.requestAndInvalidate(
      `/api/me/push-subscriptions/${encodeURIComponent(subscriptionId)}`,
      {
        method: "DELETE",
      },
      ["/api/me/bootstrap"],
    );
  }

  updateOneRM(payload) {
    return this.requestAndInvalidate(
      "/api/me/one-rm",
      {
        method: "PATCH",
        body: payload,
      },
      ["/api/me/bootstrap"],
    );
  }

  registerAndroidDeviceToken(payload) {
    return this.requestAndInvalidate(
      "/api/me/device-tokens/android",
      {
        method: "POST",
        body: payload,
      },
      ["/api/me/bootstrap"],
    );
  }

  deleteAndroidDeviceToken(installationId) {
    return this.requestAndInvalidate(
      `/api/me/device-tokens/android/${encodeURIComponent(installationId)}`,
      {
        method: "DELETE",
      },
      ["/api/me/bootstrap"],
    );
  }

  async deleteAccount() {
    const result = await this.request("/api/me/account", {
      method: "DELETE",
    });
    this.clearCache();
    return result;
  }

  getCustomExercises() {
    return this.request("/api/me/custom-exercises");
  }

  createCustomExercise(payload) {
    return this.requestAndInvalidate(
      "/api/me/custom-exercises",
      {
        method: "POST",
        body: payload,
      },
      ["/api/me/bootstrap", "/api/me/custom-exercises", "/api/exercises/"],
    );
  }

  getPrograms() {
    return this.request("/api/programs");
  }

  getProgram(programId) {
    return this.request(`/api/programs/${encodeURIComponent(programId)}`);
  }

  createProgram(payload) {
    return this.requestAndInvalidate(
      "/api/programs",
      {
        method: "POST",
        body: payload,
      },
      ["/api/me/bootstrap", "/api/programs"],
    );
  }

  updateProgram(programId, payload) {
    return this.requestAndInvalidate(
      `/api/programs/${encodeURIComponent(programId)}`,
      {
        method: "PATCH",
        body: payload,
      },
      ["/api/me/bootstrap", "/api/programs"],
    );
  }

  activateProgram(programId) {
    return this.requestAndInvalidate(
      `/api/programs/${encodeURIComponent(programId)}/activate`,
      {
        method: "POST",
        body: {},
      },
      ["/api/me/bootstrap", "/api/programs"],
    );
  }

  deleteProgram(programId) {
    return this.requestAndInvalidate(
      `/api/programs/${encodeURIComponent(programId)}`,
      {
        method: "DELETE",
      },
      ["/api/me/bootstrap", "/api/programs"],
    );
  }

  getWorkouts(limit = 30) {
    return this.request(`/api/workouts?limit=${encodeURIComponent(limit)}`);
  }

  getWorkout(workoutId) {
    return this.request(`/api/workouts/${encodeURIComponent(workoutId)}`);
  }

  getWorkoutsByDate(date) {
    return this.request(`/api/workouts/by-date/${encodeURIComponent(date)}`);
  }

  getWorkoutCalendarMonth(month) {
    return this.request(`/api/workouts/calendar?month=${encodeURIComponent(month)}`);
  }

  getWorkoutsByExercise(exerciseId) {
    return this.request(`/api/workouts/by-exercise/${encodeURIComponent(exerciseId)}`);
  }

  startWorkout(payload) {
    return this.requestAndInvalidate(
      "/api/workouts/start",
      {
        method: "POST",
        body: payload,
      },
      buildWorkoutMutationInvalidationMatchers(),
    );
  }

  syncWorkoutSets(payload) {
    return this.requestAndInvalidate(
      "/api/workouts/sync-sets",
      {
        method: "POST",
        body: payload,
      },
      buildWorkoutMutationInvalidationMatchers(),
    );
  }

  updateSet(payload) {
    return this.requestAndInvalidate(
      "/api/workouts/update-set",
      {
        method: "POST",
        body: payload,
      },
      buildWorkoutMutationInvalidationMatchers(),
    );
  }

  finishWorkout(payload) {
    return this.requestAndInvalidate(
      "/api/workouts/finish",
      {
        method: "POST",
        body: payload,
      },
      [
        ...buildWorkoutMutationInvalidationMatchers(),
        "/api/workouts/latest-item/",
        "/api/workouts/latest-items",
        "/api/analytics/volume?",
      ],
    );
  }

  searchExercises(q, options = {}) {
    const normalizedQuery = normalizeSearchQuery(q);
    if (normalizedQuery.length < 2) {
      return Promise.resolve({
        query: normalizedQuery,
        count: 0,
        items: [],
      });
    }

    return this.request(this.buildExerciseSearchPath(normalizedQuery, options));
  }

  getExercises(ids) {
    return this.request("/api/exercises/batch", {
      method: "POST",
      body: { ids },
    });
  }

  getExercise(exerciseId) {
    return this.request(`/api/exercises/${encodeURIComponent(exerciseId)}`);
  }

  addWorkoutItem(workoutId, item) {
    return this.requestAndInvalidate(
      `/api/workouts/${encodeURIComponent(workoutId)}/items`,
      {
        method: "POST",
        body: { item },
      },
      buildWorkoutMutationInvalidationMatchers(),
    );
  }

  replaceWorkoutItem(workoutId, order, item) {
    return this.requestAndInvalidate(
      `/api/workouts/${encodeURIComponent(workoutId)}/items/${encodeURIComponent(order)}`,
      {
        method: "PATCH",
        body: { item },
      },
      buildWorkoutMutationInvalidationMatchers(),
    );
  }

  addWorkoutSet(workoutId, order) {
    return this.requestAndInvalidate(
      `/api/workouts/${encodeURIComponent(workoutId)}/items/${encodeURIComponent(order)}/sets`,
      {
        method: "POST",
        body: {},
      },
      buildWorkoutMutationInvalidationMatchers(),
    );
  }

  reorderWorkoutItems(workoutId, orderSequence) {
    return this.requestAndInvalidate(
      `/api/workouts/${encodeURIComponent(workoutId)}/items/reorder`,
      {
        method: "PATCH",
        body: { orderSequence },
      },
      buildWorkoutMutationInvalidationMatchers(),
    );
  }

  removeWorkoutItem(workoutId, order) {
    return this.requestAndInvalidate(
      `/api/workouts/${encodeURIComponent(workoutId)}/items/${encodeURIComponent(order)}`,
      {
        method: "DELETE",
      },
      buildWorkoutMutationInvalidationMatchers(),
    );
  }

  removeWorkoutSet(workoutId, order, setIndex) {
    return this.requestAndInvalidate(
      `/api/workouts/${encodeURIComponent(workoutId)}/items/${encodeURIComponent(order)}/sets/${encodeURIComponent(setIndex)}`,
      {
        method: "DELETE",
      },
      buildWorkoutMutationInvalidationMatchers(),
    );
  }

  updateActiveSession(payload) {
    return this.requestAndInvalidate(
      "/api/workouts/session/active",
      {
        method: "PATCH",
        body: payload,
      },
      buildWorkoutMutationInvalidationMatchers(),
    );
  }

  commitWorkoutSession(payload) {
    const safeWorkoutId = String(payload?.workoutId || "").trim();
    const matchers = [
      ...buildWorkoutMutationInvalidationMatchers(),
      "/api/workouts/latest-item/",
      "/api/workouts/latest-items",
    ];

    if (safeWorkoutId) {
      matchers.push(`/api/workouts/${encodeURIComponent(safeWorkoutId)}`);
    }

    if (String(payload?.mutation?.type || "none") === "finish") {
      matchers.push("/api/analytics/volume?");
    }

    return this.requestAndInvalidate(
      "/api/workouts/session/commit",
      {
        method: "POST",
        body: payload,
      },
      matchers,
    );
  }

  discardActiveSession() {
    return this.requestAndInvalidate(
      "/api/workouts/session/active",
      {
        method: "DELETE",
      },
      buildWorkoutMutationInvalidationMatchers(),
    );
  }

  getLatestWorkoutItem(exerciseId, excludeWorkoutId, options = {}) {
    const query = excludeWorkoutId
      ? `?excludeWorkoutId=${encodeURIComponent(excludeWorkoutId)}`
      : "";
    return this.request(`/api/workouts/latest-item/${encodeURIComponent(exerciseId)}${query}`, {
      forceRefresh: !!options.forceRefresh,
    });
  }

  getLatestWorkoutItems(exerciseIds, excludeWorkoutId, options = {}) {
    return this.request("/api/workouts/latest-items", {
      method: "POST",
      body: {
        exerciseIds,
        excludeWorkoutId,
      },
      forceRefresh: !!options.forceRefresh,
    });
  }

  getVolumeSeries(exerciseId) {
    return this.request(`/api/analytics/volume?exerciseId=${encodeURIComponent(exerciseId)}`);
  }
}
